import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  Select,
  Divider,
} from "@mui/material";
import axios from "axios";

const API_BASE = "/api/users/";

const People = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    employment_type: "FULL_TIME",
  });

  // =========================
  // FETCH USERS
  // =========================
  const fetchUsers = async () => {
    try {
      const res = await axios.get(API_BASE);
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users", err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // =========================
  // HANDLE FORM CHANGE
  // =========================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // =========================
  // CREATE USER
  // =========================
  const handleCreate = async () => {
    try {
      await axios.post(API_BASE, form);
      setOpen(false);
      setForm({
        name: "",
        email: "",
        password: "",
        role: "",
        employment_type: "FULL_TIME",
      });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || "Error creating user");
    }
  };

  // =========================
  // UPDATE ROLE
  // =========================
  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API_BASE}${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (err) {
      alert("Error updating role");
    }
  };

  // =========================
  // UPDATE STATUS
  // =========================
  const handleStatusChange = async (userId, isActive) => {
    try {
      await axios.patch(`${API_BASE}${userId}/status`, {
        is_active: isActive,
      });
      fetchUsers();
    } catch (err) {
      alert("Error updating status");
    }
  };

  return (
    <Box sx={{ maxWidth: "1100px", mx: "auto" }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ mb: 4 }}
      >
        People Management
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Top Action Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
            }}
            onClick={() => setOpen(true)}
          >
            Create User
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Table
          sx={{
            borderCollapse: "separate",
            borderSpacing: "0 10px",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>
                Name
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>
                Role
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>
                Employment
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 700, fontSize: 14 }}
              >
                Active
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                sx={{
                  backgroundColor: "#f9fafb",
                  borderRadius: 3,
                  "& td": {
                    borderBottom: "none",
                    py: 2.5,
                  },
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                <TableCell sx={{ fontWeight: 500 }}>
                  {user.name}
                </TableCell>

                <TableCell>
                  <Select
                    value={user.role}
                    size="small"
                    sx={{
                      minWidth: 170,
                      backgroundColor: "#ffffff",
                      borderRadius: 2,
                    }}
                    onChange={(e) =>
                      handleRoleChange(user.id, e.target.value)
                    }
                  >
                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                    <MenuItem value="HR">HR</MenuItem>
                    <MenuItem value="PROJECT_MANAGER">
                      PROJECT_MANAGER
                    </MenuItem>
                    <MenuItem value="TEAM_LEAD">TEAM_LEAD</MenuItem>
                    <MenuItem value="USER">USER</MenuItem>
                  </Select>
                </TableCell>

                <TableCell sx={{ color: "#4b5563", fontWeight: 500 }}>
                  {user.employment_type}
                </TableCell>

                <TableCell align="center">
                  <Switch
                    checked={user.is_active ?? true}
                    onChange={(e) =>
                      handleStatusChange(user.id, e.target.checked)
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* CREATE USER DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Create User
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Email"
            name="email"
            value={form.email}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Password"
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Role"
            select
            name="role"
            value={form.role}
            onChange={handleChange}
          >
            <MenuItem value="ADMIN">ADMIN</MenuItem>
            <MenuItem value="HR">HR</MenuItem>
            <MenuItem value="PROJECT_MANAGER">
              PROJECT_MANAGER
            </MenuItem>
            <MenuItem value="TEAM_LEAD">TEAM_LEAD</MenuItem>
            <MenuItem value="USER">USER</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            label="Employment Type"
            select
            name="employment_type"
            value={form.employment_type}
            onChange={handleChange}
          >
            <MenuItem value="FULL_TIME">FULL_TIME</MenuItem>
            <MenuItem value="CONTRACT">CONTRACT</MenuItem>
            <MenuItem value="INTERN">INTERN</MenuItem>
          </TextField>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={handleCreate}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default People;